<script lang="ts">
export default {
  name: 'BinderUploadPanel',
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { FileUp, Loader, AlertTriangle, X } from '@lucide/vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { binderFileProblem, externalPolicyBinderService } from '@/services/v2/ExternalPolicyBinderService';
import { aiService } from '@/services/AIService';
import { BinderBatchResult, MAX_BINDERS_PER_BATCH } from '@/types/ExternalPolicyBinder';
import { AIPrompt } from '@/types/ai';
import { useToast } from '@/composables/useToast';

const props = defineProps<{
  accountId: string;
}>();

// A duplicate only ever yields the id of the submission already on file, so that is all
// this promises; the review route is all either outcome is used for.
const emit = defineEmits<{
  submitted: [submission: { submissionId: number }];
  finished: [];
}>();

const toast = useToast();
const draggingOver = ref(false);
// Which prompt reads the binder decides which fields come out of it, so the broker picks
// rather than inheriting whichever external prompt happened to be published last.
const prompts = ref<AIPrompt[]>([]);
const promptVersionId = ref<number | null>(null);
const loadingPrompts = ref(true);

const promptOptions = computed(() =>
  prompts.value.map((prompt) => ({
    label: prompt.version === 'DRAFT' ? prompt.name : `${prompt.name} (v${prompt.version})`,
    value: String(prompt.promptVersionId),
  })),
);

const hasPrompts = computed(() => prompts.value.length > 0);
const isSubmitting = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const selectedFiles = ref<File[]>([]);
const results = ref<BinderBatchResult[] | null>(null);

const sendableCount = computed(() => selectedFiles.value.filter((file) => !binderFileProblem(file)).length);

function sameFile(a: File, b: File) {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

function addFiles(files: File[]) {
  if (!promptVersionId.value) {
    toast.error('Choose an extraction prompt before uploading a binder.');
    return;
  }
  const fresh = files.filter((file) => !selectedFiles.value.some((chosen) => sameFile(chosen, file)));
  const alreadyChosen = files.length - fresh.length;
  if (alreadyChosen > 0) {
    toast.warning(
      alreadyChosen === 1
        ? 'That binder is already in this upload, so it was not added again.'
        : `${alreadyChosen} of those binders are already in this upload, so they were not added again.`,
    );
  }
  const room = Math.max(MAX_BINDERS_PER_BATCH - selectedFiles.value.length, 0);
  const overflow = fresh.length - room;
  if (overflow > 0) {
    toast.error(
      `You can upload up to ${MAX_BINDERS_PER_BATCH} binders at a time. ${overflow} file${overflow === 1 ? ' was' : 's were'} not added.`,
    );
  }
  selectedFiles.value = [...selectedFiles.value, ...fresh.slice(0, room)];
}

function removeFile(index: number) {
  selectedFiles.value = selectedFiles.value.filter((_, i) => i !== index);
}

// A retry sends only the files that failed, so its outcomes are folded back into the
// report rather than replacing it -- the binders that went through keep their review links.
function foldInRetried(previous: BinderBatchResult[], retried: BinderBatchResult[]) {
  return previous.map((prior) => retried.find((fresh) => sameFile(fresh.file, prior.file)) ?? prior);
}

async function submitSelected() {
  if (isSubmitting.value || sendableCount.value === 0 || !promptVersionId.value) {
    return;
  }

  const previous = results.value;
  isSubmitting.value = true;
  try {
    const batch = await externalPolicyBinderService.submitBinders(
      props.accountId,
      selectedFiles.value,
      promptVersionId.value,
    );
    // A lone binder keeps the original single-file flow: straight to its review.
    if (!previous && batch.length === 1 && batch[0].outcome === 'queued') {
      emit('submitted', batch[0].submission);
      return;
    }
    const merged = previous ? foldInRetried(previous, batch) : batch;
    results.value = merged;
    // Anything that failed for a reason re-picking the file wouldn't change stays selected,
    // so retrying it doesn't mean choosing every binder again.
    selectedFiles.value = merged
      .filter((result) => result.outcome === 'rejected' && result.retryable)
      .map((result) => result.file);
  } finally {
    isSubmitting.value = false;
  }
}

function startOver() {
  results.value = null;
  selectedFiles.value = [];
}

function openResult(result: BinderBatchResult) {
  if (result.outcome === 'queued') {
    emit('submitted', result.submission);
  } else if (result.outcome === 'duplicate' && result.existingSubmissionId) {
    emit('submitted', { submissionId: result.existingSubmissionId });
  }
}

function formatSize(bytes: number) {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function triggerFilePicker() {
  if (!promptVersionId.value || isSubmitting.value) {
    return;
  }
  fileInput.value?.click();
}

function handleFileChosen(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  input.value = '';
  if (files.length) {
    addFiles(files);
  }
}

function handleDragOver(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = true;
}

function handleDragLeave(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;
}

function handleDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;
  if (isSubmitting.value) {
    return;
  }

  const files: File[] = [];
  if (e.dataTransfer?.items) {
    for (const item of e.dataTransfer.items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
  }
  if (!files.length && e.dataTransfer?.files?.length) {
    files.push(...Array.from(e.dataTransfer.files));
  }
  if (files.length) {
    addFiles(files);
  }
}

// Only published external prompts can extract a binder; a draft has no model settled yet.
onMounted(async () => {
  try {
    const all = await aiService.getAllPrompts('external');
    prompts.value = all.filter((prompt) => !prompt.isDraft);
    if (prompts.value.length === 1) {
      promptVersionId.value = prompts.value[0].promptVersionId;
    }
  } catch {
    prompts.value = [];
  } finally {
    loadingPrompts.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-base-content/60 italic">
      Upload binder PDFs and we'll extract the policy details automatically. You'll review each one before a policy
      is created.
    </p>

    <div
      v-if="loadingPrompts"
      class="flex items-center gap-2 text-sm"
      data-testid="binder-prompts-loading">
      <Loader :size="16" class="animate-spin text-secondary" />
      <span>Loading extraction prompts…</span>
    </div>

    <!-- Nothing can read a binder until someone has published an external prompt, so say
         that rather than offering an upload that would be rejected. -->
    <div
      v-else-if="!hasPrompts"
      class="alert alert-warning"
      data-testid="binder-no-prompts">
      <AlertTriangle :size="18" />
      <span>
        No published external extraction prompt exists yet. One has to be created and published in the AI prompt
        manager before a binder can be scanned.
      </span>
    </div>

    <div
      v-else-if="results"
      class="flex flex-col gap-3"
      data-testid="binder-batch-results">
      <ul class="flex flex-col divide-y divide-base-300 rounded-lg border border-base-300">
        <li
          v-for="(result, index) in results"
          :key="index"
          class="flex items-center gap-3 px-3 py-2 text-sm"
          :data-testid="`binder-result-${index}`">
          <span class="min-w-0 flex-1 truncate" :title="result.file.name">{{ result.file.name }}</span>
          <template v-if="result.outcome === 'queued'">
            <span class="badge badge-sm badge-success shrink-0">Queued</span>
            <button class="link text-xs shrink-0" @click="openResult(result)">Review</button>
          </template>
          <template v-else-if="result.outcome === 'duplicate'">
            <span class="badge badge-sm badge-warning shrink-0">Already submitted</span>
            <button
              v-if="result.existingSubmissionId"
              class="link text-xs shrink-0"
              @click="openResult(result)">
              View existing
            </button>
          </template>
          <template v-else>
            <span class="badge badge-sm badge-error shrink-0">Rejected</span>
            <span class="text-xs text-error shrink-0">{{ result.reason }}</span>
          </template>
        </li>
      </ul>
      <div class="flex gap-2">
        <button
          v-if="selectedFiles.length"
          class="btn btn-sm btn-secondary"
          :disabled="isSubmitting"
          data-testid="binder-retry-failed"
          @click="submitSelected">
          <Loader v-if="isSubmitting" :size="14" class="animate-spin" />
          Retry {{ selectedFiles.length }} failed
        </button>
        <button
          class="btn btn-sm"
          :class="selectedFiles.length ? 'btn-ghost' : 'btn-secondary'"
          data-testid="binder-view-pending"
          @click="emit('finished')">
          View pending scans
        </button>
        <button
          class="btn btn-sm btn-ghost"
          data-testid="binder-upload-more"
          @click="startOver">
          Upload more
        </button>
      </div>
    </div>

    <template v-else>
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium">Extraction prompt</span>
        <AppSelect
          :model-value="promptVersionId ? String(promptVersionId) : ''"
          :options="promptOptions"
          data-testid="binder-prompt-select"
          @update:model-value="(value: string) => (promptVersionId = value ? Number(value) : null)" />
        <span class="text-xs text-base-content/60">Decides which fields are pulled from every binder in this upload.</span>
      </label>

      <div
        class="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 transition-colors"
        :class="[
          draggingOver ? 'border-secondary bg-secondary/5' : 'border-base-300',
          promptVersionId ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
        ]"
        data-testid="binder-drop-zone"
        @click="triggerFilePicker"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop">
        <Loader v-if="isSubmitting" :size="32" class="animate-spin text-secondary" />
        <FileUp v-else :size="32" class="text-secondary" />
        <div class="text-center">
          <p v-if="isSubmitting" class="font-medium">Uploading binders…</p>
          <template v-else-if="!promptVersionId">
            <p class="font-medium">Choose an extraction prompt first</p>
            <p class="text-sm text-base-content/60">Then drop binder PDFs here</p>
          </template>
          <template v-else>
            <p class="font-medium">Drop binder PDFs here or click to browse</p>
            <p class="text-sm text-base-content/60">
              PDF only, up to 25 MB each, {{ MAX_BINDERS_PER_BATCH }} at a time
            </p>
          </template>
        </div>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          accept="application/pdf,.pdf"
          multiple
          data-testid="binder-file-input"
          @click.stop
          @change="handleFileChosen" />
      </div>

      <div
        v-if="selectedFiles.length"
        class="flex flex-col gap-3"
        data-testid="binder-selected-files">
        <ul class="flex flex-col divide-y divide-base-300 rounded-lg border border-base-300">
          <li
            v-for="(file, index) in selectedFiles"
            :key="`${file.name}-${file.size}-${file.lastModified}`"
            class="flex items-center gap-3 px-3 py-2 text-sm"
            :data-testid="`binder-selected-${index}`">
            <span class="min-w-0 flex-1 truncate" :title="file.name">{{ file.name }}</span>
            <span class="shrink-0 text-xs text-base-content/60">{{ formatSize(file.size) }}</span>
            <span v-if="binderFileProblem(file)" class="shrink-0 text-xs text-error">
              {{ binderFileProblem(file) }}
            </span>
            <button
              type="button"
              class="btn btn-ghost btn-xs btn-square shrink-0"
              :aria-label="`Remove ${file.name}`"
              :disabled="isSubmitting"
              :data-testid="`binder-remove-${index}`"
              @click="removeFile(index)">
              <X :size="14" />
            </button>
          </li>
        </ul>
        <button
          class="btn btn-sm btn-secondary w-fit"
          :disabled="isSubmitting || sendableCount === 0"
          data-testid="binder-submit"
          @click="submitSelected">
          <Loader v-if="isSubmitting" :size="14" class="animate-spin" />
          Scan {{ sendableCount }} binder{{ sendableCount === 1 ? '' : 's' }}
        </button>
      </div>
    </template>
  </div>
</template>
